import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Megaphone, Loader2, Send, Clock } from "lucide-react";
import { DateTimePicker } from "@/components/DateTimePicker";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { TemplateStrip } from "./sms-broadcast/TemplateStrip";
import { ScheduledStrip } from "./sms-broadcast/ScheduledStrip";
import { AudienceControls } from "./sms-broadcast/AudienceControls";
import { PreviewPanel } from "./sms-broadcast/PreviewPanel";
import { ConfirmDialog } from "./sms-broadcast/ConfirmDialog";

/**
 * SMS Broadcast — 2-step preview + confirm flow via
 *   POST /api/ghl/sms-broadcast-preview   (non-destructive)
 *   POST /api/ghl/sms-broadcast           (fires immediately)
 *   POST /api/ghl/sms-broadcast/schedule  (queues for later)
 *
 * State + orchestration live here; every child in `./sms-broadcast/` is
 * pure UI.
 */
export function SmsBroadcastCard() {
  const { currentProperty } = useProperty();
  const { user } = useAuth();
  const { subscribe } = useRealtime();

  const [body, setBody] = useState(
    "Community update — see /resident for details. Reply STOP to opt out.",
  );
  const [scopeAll, setScopeAll] = useState(false);
  const [includeVendors, setIncludeVendors] = useState(false);
  const [consentOnly, setConsentOnly] = useState(true);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return d;
  });
  const [scheduled, setScheduled] = useState([]);

  const bodyLen = body.length;
  const segments = useMemo(() => Math.max(1, Math.ceil(bodyLen / 160)), [bodyLen]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/ghl/sms-broadcast-templates");
      setTemplates(data?.items || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/SmsBroadcastCard.jsx: swallowed', err); }
  };

  const loadScheduled = async () => {
    try {
      const { data } = await api.get("/ghl/sms-broadcast/schedule");
      setScheduled(data?.pending || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/SmsBroadcastCard.jsx: swallowed', err); }
  };

  useEffect(() => { loadTemplates(); loadScheduled(); }, []);

  // R72 — live sync when a scheduled broadcast fires so the "Scheduled"
  // list flips from pending → gone without a manual reload.
  useEffect(() => {
    if (!user?.id) return;
    const unsub = subscribe(`user:${user.id}`, ({ event }) => {
      if (event === "broadcast.fired") { loadScheduled(); loadTemplates(); }
    });
    return unsub;
  }, [subscribe, user?.id]);

  const applyTemplate = (t) => {
    setBody(t.body);
    setPreview(null);
    toast.info(`Loaded template (${t.use_count}× used)`);
  };

  const rehearse = async () => {
    if (!body.trim()) { toast.error("Message body is empty"); return; }
    if (!user?.phone) { toast.error("No phone on your profile — set one in Settings first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ghl/sms-test", {
        to: user.phone, body: body.trim(),
        rehearsal_of: body.slice(0, 60),
      });
      if (data.ok) toast.success(`Rehearsal sent to •••${data.to_last4} — check your phone`);
      else if (data.queued) toast.warning(`Queued — ${data.error || "GHL sync issue"}`);
      else toast.error(data.error || "Rehearsal failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rehearsal failed");
    } finally { setBusy(false); }
  };

  const roles = useMemo(() => {
    const r = ["resident"];
    if (includeVendors) r.push("vendor");
    return r;
  }, [includeVendors]);

  const buildPayload = () => ({
    property_id: scopeAll ? null : currentProperty?.id,
    roles, consent_only: consentOnly, body: body.trim(),
  });

  const doPreview = async () => {
    if (!body.trim()) { toast.error("Message body is empty"); return; }
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post("/ghl/sms-broadcast-preview", buildPayload());
      setPreview(data);
      if (data.recipient_count === 0) toast.warning("No recipients match — adjust the audience filters");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true); setConfirmOpen(false);
    try {
      if (scheduleOn) {
        const iso = (scheduleAt instanceof Date ? scheduleAt : new Date(scheduleAt)).toISOString();
        await api.post("/ghl/sms-broadcast/schedule", { ...buildPayload(), scheduled_for: iso });
        toast.success(`Scheduled for ${new Date(iso).toLocaleString()}`);
        setPreview(null);
        loadScheduled();
      } else {
        const { data } = await api.post("/ghl/sms-broadcast", buildPayload());
        setLastRun(data); setPreview(null);
        toast.success(`Broadcast fired — sent ${data.sent}, queued ${data.queued}, failed ${data.failed}`);
        loadTemplates();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const markDirty = () => setPreview(null);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="sms-broadcast-card">
      <div className="flex items-center gap-2 overline mb-3">
        <Megaphone className="h-4 w-4 text-orange-400" /> Bulk SMS Broadcast
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        Draft a community-wide SMS. Preview shows recipient count + estimated
        GHL cost before you fire. Non-consented residents are always excluded.
      </p>

      <TemplateStrip templates={templates} setTemplates={setTemplates} onApply={applyTemplate} />
      <ScheduledStrip scheduled={scheduled} setScheduled={setScheduled} />

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between">
            <Label>Message</Label>
            <span className="text-[11px] text-zinc-500" data-testid="sms-broadcast-charcount">
              {bodyLen} chars · {segments} segment{segments === 1 ? "" : "s"}
            </span>
          </div>
          <Textarea data-testid="sms-broadcast-body" className="min-h-[80px]"
                    value={body} onChange={(e) => { setBody(e.target.value); markDirty(); }}
                    disabled={busy} />
        </div>

        <AudienceControls scopeAll={scopeAll} setScopeAll={setScopeAll}
                           includeVendors={includeVendors} setIncludeVendors={setIncludeVendors}
                           consentOnly={consentOnly} setConsentOnly={setConsentOnly}
                           currentPropertyName={currentProperty?.name}
                           busy={busy}
                           onDirty={markDirty} />

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1"
               data-testid="sms-broadcast-schedule-toggle-wrap">
            <Clock className="h-3.5 w-3.5 text-orange-400" />
            <Label className="text-xs cursor-pointer" htmlFor="schedule-toggle">Schedule for later</Label>
            <Switch id="schedule-toggle" data-testid="sms-broadcast-schedule-toggle"
                    checked={scheduleOn} onCheckedChange={setScheduleOn} disabled={busy} />
          </div>
          {scheduleOn && (
            <div data-testid="sms-broadcast-schedule-picker">
              <DateTimePicker value={scheduleAt} onChange={setScheduleAt}
                               testIdPrefix="sms-broadcast-dt" />
            </div>
          )}
          <Button data-testid="sms-broadcast-preview"
                  onClick={doPreview} disabled={busy || !body.trim()}
                  variant="outline" className="rounded-full border-white/10">
            {busy && !confirmOpen
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Previewing…</>
              : <>Preview recipients</>}
          </Button>
          <Button data-testid="sms-broadcast-rehearse" onClick={rehearse}
                  disabled={busy || !body.trim() || !user?.phone}
                  variant="outline"
                  className="rounded-full border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
                  title={user?.phone
                    ? `Send only to your phone ending in •••${(user.phone.match(/\d{4}$/) || [""])[0]}`
                    : "No phone on your profile"}>
            Rehearse to me
          </Button>
          {preview && (
            <Button data-testid="sms-broadcast-send"
                    onClick={() => setConfirmOpen(true)}
                    disabled={busy || preview.recipient_count === 0}
                    className="rounded-full bg-orange-500 hover:bg-orange-600 text-black">
              <Send className="mr-2 h-4 w-4" />
              {scheduleOn ? `Schedule for ${preview.recipient_count}` : `Send to ${preview.recipient_count}`}
            </Button>
          )}
        </div>

        <PreviewPanel preview={preview} />

        {lastRun && (
          <div data-testid="sms-broadcast-result"
               className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <div className="font-medium text-emerald-300">Broadcast complete</div>
            <div className="text-xs text-zinc-400 mt-1">
              Sent {lastRun.sent} · Queued {lastRun.queued} · Failed {lastRun.failed} ·
              Est cost ${lastRun.estimated_cost_usd.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen}
                      scheduleOn={scheduleOn} preview={preview}
                      scheduleAt={scheduleAt} onConfirm={doSend} />
    </Card>
  );
}

export default SmsBroadcastCard;
